import { createAxiosInstance } from './createAxiosInstance';
import { API_ROUTES } from './httpRequest';

const axiosInstance = createAxiosInstance(import.meta.env.REACT_APP_HYPERTRACKER_URL);

export const HT_API_ROUTES = {
    ALPHA_FEED: {
        PREFIX: 'alpha-feed/',
        SUMMARY: 'alpha-feed/summary',
        PULSE: 'alpha-feed/pulse',
        POSITIONS: 'alpha-feed/positions',
        POSITION: (positionId: string, alphaSegmentId: string) =>
            `alpha-feed/positions/${positionId}/segments/${alphaSegmentId}`,
        POSITION_EVENTS: (positionId: string) => `alpha-feed/positions/${positionId}/events`,
        QUALIFICATION_TRADES: (positionId: string, alphaSegmentId: string) =>
            `alpha-feed/positions/${positionId}/segments/${alphaSegmentId}/qualification-trades`,
        RECENT_OUTCOMES: (positionId: string) =>
            `alpha-feed/positions/${positionId}/alpha-trader/recent-outcomes`,
        CREATE_TRADE: (positionId: string, alphaSegmentId: string, eventId: string) =>
            `alpha-feed/positions/${encodeURIComponent(positionId)}/segments/${encodeURIComponent(alphaSegmentId)}/events/${encodeURIComponent(eventId)}/trades`,
        TRADE: (tradeId: string) => `alpha-feed/trades/${encodeURIComponent(tradeId)}`,
        TRADING_LIFECYCLES: 'alpha-feed/trading-lifecycles',
        REWARDS: 'alpha-feed/rewards',
    },
    TRADING: {
        CONFIG: 'trading/config',
        AGENT: 'trading/agent',
        CLOSE_POSITION: 'trading/positions/close',
    },
    MY_ACCOUNTS: 'my/accounts?my=true&vault=false',
    ALERTS: {
        ROOT: 'alerts',
        ROOT_SLASH: '/alerts',
        ITEM_SLASH: (id: number) => `/alerts/${id}`,
        QUOTA: 'alerts/quota',
        STATS: 'alerts/stats',
    },
    FAVORITES: {
        ACCOUNTS: 'favorites/accounts',
        ACCOUNTS_DETAIL: 'favorites/accounts-detail',
        POSITIONS: 'favorites/positions',
        POSITIONS_DETAIL: 'favorites/positions-detail',
        COINS: 'favorites/coins',
        LIMITS: 'favorites/limits',
        LIMITS_UPDATED: 'favorites/limits/updated',
    },
    MY: {
        ACCOUNTS: 'my/accounts',
        ACCOUNT_UPDATED: (address: string) => `my/accounts/${address}/updated`,
        VAULTS: 'my/vaults',
    },
    LEADERBOARDS: {
        PERP_PNL: 'leaderboards/perp-pnl',
        ALL_PNL: 'leaderboards/all-pnl',
        AVG_DAILY_PERP_PNL: 'leaderboards/avg-daily-perp-pnl',
        RICH_LIST: 'leaderboards/rich-list',
        RICH_LIST_SLASH: '/leaderboards/rich-list',
        TOP_CLAIMED: 'leaderboards/top-claimed',
        TOP_CLAIMED_SLASH: '/leaderboards/top-claimed',
        TOP_CLAIMED_MY: 'leaderboards/top-claimed/my',
        TOP_CLAIMED_MY_SLASH: '/leaderboards/top-claimed/my',
        MOST_FAVORITED: 'leaderboards/most-favorited',
        MOST_FAVORITED_SLASH: '/leaderboards/most-favorited',
        MOST_FAVORITED_MY: 'leaderboards/most-favorited/my',
        MOST_FAVORITED_MY_SLASH: '/leaderboards/most-favorited/my',
        VAULTS: 'leaderboards/vaults',
        VAULTS_SLASH: '/leaderboards/vaults',
        VAULTS_MY: 'leaderboards/vaults/my',
        VAULTS_MY_SLASH: '/leaderboards/vaults/my',
        REFERRERS: 'leaderboards/referrers',
        REFERRERS_MY: 'leaderboards/referrers/my',
    },
    HYPE: {
        HOLDERS: 'hype/holders',
        HOLDERS_SLASH: '/hype/holders',
        HOLDERS_MY: 'hype/holders/my',
        HOLDERS_MY_SLASH: '/hype/holders/my',
    },
    POSITIONS: {
        ROOT: 'positions',
        ROOT_SLASH: '/positions',
        HEATMAP: 'positions/heatmap',
        RANKED: 'positions/ranked',
        RANKED_SLASH: '/positions/ranked',
        CLOSE_TO_LIQUIDATION_SLASH: '/positions/close-to-liquidation',
        COIN_SEGMENTS: (coin: string) => `positions/coins/${coin}/segments`,
    },
    SEGMENTS: {
        SUMMARY: (segmentId: number) => `segments/${segmentId}/summary`,
        POSITIONS: (segmentId: string | number | null) => `segments/${segmentId}/positions`,
        POSITIONS_LARGEST: (segmentId: number) => `segments/${segmentId}/positions/largest`,
        ASSETS_LIQUIDATION_RISK: (segmentId: number) =>
            `segments/${segmentId}/assets/liquidation-risk`,
        WALLETS: (segmentId: number) => `segments/${segmentId}/wallets`,
    },
    LIQUIDATIONS: {
        SUMMARY: 'liquidations/summary',
        HEATMAP: 'liquidations/heatmap',
        STATS: 'liquidations/stats',
        EVENTS: 'liquidations/events',
        WALLET_WIPES: 'liquidations/wallet-wipes',
    },
    CLOSED_TRADES: {
        ROOT: 'closed-trades',
        SUMMARY: 'closed-trades/summary',
        BATCH_FILLS: (hash: string | undefined) => `closed-trades/${hash}/batch-fills`,
        CANDLES: 'candles',
    },
    BUILDERS: {
        USERS: (builder: string | undefined) => `builders/${builder}/users`,
    },
    CONSUMER: {
        ROOT: 'consumer',
        METRICS: 'consumer/metrics',
        ENDPOINT_METRICS_SLASH: '/consumer/endpoint-metrics',
        CREATE_SLASH: '/consumer/create',
        TOKEN_SLASH: '/consumer/token',
        WEBHOOK_SLASH: '/consumer/webhook',
    },
    SEARCH: {
        ROOT: 'search',
    },
    GUEST: {
        TOKEN: 'guest/token',
        SEARCH: 'guest/search',
    },
    REFERRALS: {
        REFERRERS_SLASH: '/referrals/referrers',
        REFERRERS_MY_SLASH: '/referrals/referrers/my',
        BASED_TRADE_PROGRESS_SLASH: '/based-referral/check/progress',
        HL_TRADE_PROGRESS_SLASH: '/hyperliquid-referral/check/progress',
    },
    WALLETS: {
        ITEM: (walletAddress: string) => `wallets/${walletAddress}`,
        TRENDING: 'trending-wallets',
    },
    ME_SLASH: '/me',
    TELEGRAM_CONNECTION: `${API_ROUTES.TELEGRAM}/connection`,
    EVENTS: {
        ALPHA_FEED: 'events/alpha-feed',
        LIQUIDATIONS: 'events/liquidations',
    },
};

export default axiosInstance;
